<template>
    <div v-if="product" v-show="isVisible" class="amount-input-wrapper">
        <v-layout column class="ma-0 pa-0">
            <v-flex
                :style="tierPriceStyle"
                :order-xs1="tierPricePosition === 'top'"
                :order-xs3="tierPricePosition === 'bottom'"
                class="ma-0 pa-0"
            >
                <tier-prices
                    v-if="!hideTierPriceInfo"
                    :arrow-on-bottom="isBasketField"
                    :product-id="currentProductId"
                    :product="product"
                    :merchant="currentMerchantId"
                    class="tier-price-tooltip"
                    :class="tierPriceAlignment"
                    ref="tierPrice"
                    :height.sync="tierPriceHeightLocal"
                    :width.sync="tierPriceWidth"
                    :quantity-in-basket="isBasketField? value : undefined"
                />
            </v-flex>

            <v-flex
                v-if="!hideTierPriceInfo"
                order-xs2
                class="ma-0 pa-0 d-flex justify-center tier-price-arrow-wrap"
                :class="{[tierPricePosition]: true, 'absolute': tierPriceAbsolute}"
            >
                <v-icon
                    v-if="tierPriceHeightLocal && tierPriceWidth"
                    :color="getTransparentColor($vuetify.theme.primary)"
                    size="35"
                    class="tier-price-arrow pa-0"
                >
                    arrow_drop_down
                </v-icon>
            </v-flex>

            <v-flex :order-xs1="tierPricePosition === 'bottom'" :order-xs3="tierPricePosition === 'top'" class="ma-0 pa-0">
                <v-combobox
                    v-if="product.type && product.type == 'coupon'"
                    class="amount-input"
                    :solo="solo"
                    hide-details
                    v-model="quantityValue"
                    type="number"
                    min="1"
                    :items="couponValue"
                    @change="onComboChange"
                    @input="onComboChange"
                />

                <v-text-field
                    v-else
                    class="amount-input"
                    :class="computedTextFieldClass"
                    :label="label"
                    v-model="quantityValue"
                    @key.enter="changeQuantity"
                    @change="changeQuantity"
                    append-outer-icon="add"
                    @click:append-outer="increaseQuantity"
                    prepend-icon="remove"
                    @click:prepend="decreaseQuantity"
                    :type="inputType || ($vuetify.breakpoint.mdAndUp ? 'text' : 'number')"
                    min="1"
                    :solo="solo"
                    :error="errorMessage"
                    :error-messages="errorMessage"
                    :hide-details="!errorMessage"
                />
            </v-flex>
        </v-layout>
        <out-of-stock-dialog v-model="quotaExceededModal" :buyable-amount="buyableAmount" />
    </div>
</template>

<script>
//@todo: Unify the flow for product page and external usage (do all checks also when it's used within prop).
import { mapGetters } from 'vuex';
import OutOfStockDialog from './OutOfStockDialog';

export default {
    name: 'AmountInput',
    components: {
        OutOfStockDialog,
    },
    props: {
        value: {
            type: String | Number,
            default: 1,
        },
        product: {
            type: Object,
        },
        currentMerchantId: {
            type: String,
        },
        currentProductId: {
            type: String | Number,
        },
        solo: {
            type: Boolean,
            default: true,
        },
        label: {
            default: undefined,
        },
        inputType: String,
        textFieldClass: {
            default: () => [],
        },
        isBasketField: {
            type: Boolean,
            default: false,
        },
        bordered: {
            type: Boolean,
            default: true,
        },
        tierPricePosition: {
            type: String,
            default: 'bottom',
        },
        tierPriceAlignment: {
            type: String,
            default: 'left',
        },
        tierPriceAbsolute: Boolean,
        tierPriceHeight: {},
        hideTierPriceInfo: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            quantityValue: 1,
            quotaExceededModal: false,
            errorMessage: null,
            tierPriceHeightLocal: 0,
            tierPriceWidth: 0,
        };
    },
    created() {
        this.quantityValue = this.value;
    },
    computed: {
        ...mapGetters(['getTransparentColor']),
        ...mapGetters('Product', [
            'isProductBuyable',
            'merchantQuota',
            'isSellOutOfStock',
            'getProductAttributeByLanguage',
        ]),
        couponValue() {
            return Object.keys(this.getProductAttributeByLanguage(this.product, 'coupon_value'));
        },
        buyableAmount() {
            return this.merchantQuota(this.product, this.currentProductId, this.currentMerchantId);
        },
        isVisible() {
            if (this.value !== undefined) {
                return !this.currentProductId || this.isProductBuyable(this.product, this.currentMerchantId, this.currentProductId);
            }
            return false;
        },
        computedTextFieldClass() {
            let computedTextFieldClass = {
                bordered: this.bordered,
            };

            if (this.textFieldClass) {
                if (typeof this.textFieldClass === 'string') {
                    computedTextFieldClass[this.textFieldClass] = true;
                } else if (Array.isArray(this.textFieldClass)) {
                    this.textFieldClass.forEach(i => computedTextFieldClass[i] = true);
                } else if (typeof this.textFieldClass === 'object') {
                    computedTextFieldClass = {
                        ...this.textFieldClass,
                        ...computedTextFieldClass,
                    };
                }
            }

            return computedTextFieldClass;
        },
        tierPriceStyle() {
            return {
                [this.tierPricePosition]: -(this.tierPriceHeightLocal + 20) + 'px',
                position: this.tierPriceAbsolute ? 'absolute' : 'initial',
            };
        },
    },
    methods: {
        onComboChange() {
            if (this.quantityValue >= 1) {
                this.changeQuantity();
            } else {
                this.$nextTick(() => {
                    this.quantityValue = 1;
                });
            }
        },
        increaseQuantity() {
            if (
                this.value !== undefined ||
                this.merchantQuota(this.product, this.currentProductId, this.currentMerchantId) === null ||
                this.quantityValue < this.merchantQuota(this.product, this.currentProductId, this.currentMerchantId) ||
                this.isSellOutOfStock(this.product, this.currentProductId, this.currentMerchantId)
            ) {
                this.quantityValue++;
                this.changeQuantity();
            } else {
                this.quotaExceededModal = true;
            }
        },
        decreaseQuantity() {
            if (this.quantityValue > 1) {
                this.quantityValue--;
                this.changeQuantity();
            }
        },
        changeQuantity() {
            this.quantityValue = parseInt(this.quantityValue);
            let limitPerOrder = 0;
            let showError = false;

            if (this.isBasketField) {
                limitPerOrder = this.$basket.getLimitPerOrder(
                    this.quantityValue,
                    this.isBasketField,
                    this.product,
                    this.currentProductId,
                    this.currentMerchantId,
                );
                if (limitPerOrder > 0) {
                    showError = true;
                } else {
                    showError = false;
                }
                if (limitPerOrder > 0) {
                    this.$emit('update:value', limitPerOrder);
                }
            }

            if (showError) {
                this.errorMessage = this.$language.translate('LIMIT_PER_ORDER_REACHED_MESSAGE');
            } else {
                this.errorMessage = null;
            }

            if (limitPerOrder === 0 && this.quantityValue > 0) {
                const quota = this.merchantQuota(this.product, this.currentProductId, this.currentMerchantId);
                if (this.currentMerchantId && this.currentProductId && quota) {
                    if (
                        this.quantityValue <= quota ||
                        this.isSellOutOfStock(this.product, this.currentProductId, this.currentMerchantId)
                    ) {
                        this.$emit('update:value', this.quantityValue);
                    } else {
                        this.quantityValue = quota;
                        this.$emit('update:value', this.quantityValue);
                        this.quotaExceededModal = true;
                    }
                } else {
                    this.$emit('update:value', this.quantityValue);
                }
            } else {
                if (limitPerOrder) {
                    this.quantityValue = limitPerOrder;
                } else {
                    this.quantityValue = this.value ? this.value : 1;
                }
            }
        },
    },
    watch: {
        value() {
            if (this.value != this.quantityValue) {
                this.quantityValue = this.value;
            }
        },
        tierPriceHeightLocal(val) {
            this.$emit('update:tierPriceHeight', val);
        },
    },
};
</script>

<style scoped lang="stylus">
.amount-input-wrapper {
    position: relative;

    .tier-price-tooltip {
        &.center {
            left: 50%;
            transform: translate(-50%, 0);
        }

        &.right {
            right: 0;
        }

        &.left {
            left: 0;
        }
    }

    .tier-price-arrow-wrap {
        &.absolute {
            position: absolute;
            left: 50%;
            transform: translate(-50%, 0);

            &.bottom {
                transform: translate(-50%, 0) rotate(180deg);
            }
        }

        &.bottom {
            bottom: -15px;
        }

        &.top {
            top: -15px;
        }

        .tier-price-arrow {
            height: 7px;
        }
    }
}
</style>

<style lang="stylus">
    .amount-input {
        background: white;
        width: 100px;

        &.bordered {
            border: 1px solid var(--v-light-darken1);
            border-radius: 25px;
        }

        .v-input__prepend-outer,
        .v-input__append-outer {
            margin-right: 0;
            margin-left: 0;
            margin-top: 10px !important;

            .v-icon {
                color: var(--v-primary-base) !important;
                font-size: 20px;
            }
        }

        input {
            text-align: center;
        }

        .v-input__control {
            .v-input__slot {
                width: auto;
            }
            width: auto;
            flex-grow: unset;
            height: 42px;
            min-height: 42px !important;
        }

        .v-input__slot {
            box-shadow: none !important;
            width: 50px;
            height: 50px;
            padding: 0 !important;
        }
    }
</style>
